import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { MarkdownPreview } from '../../../../markdown-preview';

type FileWriteData = {
  filePath?: string;
  content?: string;
};

type FileWriteRendererProps = {
  data: FileWriteData;
  maxHeight?: string;
};

export function FileWriteRenderer({
  data,
  maxHeight = '200px',
}: FileWriteRendererProps) {
  const { filePath, content } = data;

  // Check if it's a markdown file
  const isMarkdownFile = filePath?.toLowerCase().endsWith('.md') || false;
  const fileExtension = filePath?.split('.').pop()?.toUpperCase() || 'FILE';

  const renderContent = () => {
    if (!content) {
      return (
        <Text fontSize="xs" color="fg.muted">
          {'No content available'}
        </Text>
      );
    }

    if (isMarkdownFile) {
      return <MarkdownPreview source={content} fontSize={12} compact />;
    } else {
      return (
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.subtle"
          whiteSpace="pre-wrap"
        >
          {content}
        </Text>
      );
    }
  };

  return (
    <VStack alignItems="start" gap={2} width="100%">
      <HStack alignItems="center" gap={2} flex={1} minWidth={0}>
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {'File Written'}
        </Text>
        {filePath && (
          <>
            <Badge size="sm" variant="outline">
              {fileExtension}
            </Badge>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.muted"
              truncate
              maxWidth="200px"
            >
              {filePath}
            </Text>
          </>
        )}
      </HStack>
      {content && (
        <Box
          bg="bg.canvas"
          p={2}
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border.subtle"
          width="100%"
          maxHeight={maxHeight}
          overflow="auto"
          position="relative"
        >
          {renderContent()}
        </Box>
      )}
    </VStack>
  );
}
